/**
 * AI Product Advisor — Floating chat widget for Kent Traders
 * Replaces Shopify Inbox with AI-powered product recommendations,
 * BS 7671 compliance advice, and cross-sell suggestions.
 * v1.2.0 — 2026-06-27 (accuracy fix: system prompt now reflects the REAL catalogue
 *           — British General (BG) electrical specialist + small hotel/fire/bathroom
 *           ranges; removed brands we do not sell; hard no-fabrication rule for
 *           codes/prices/stock; "Find a product" is now a static, no-AI category menu.)
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'kt_advisor_history';
  var MAX_HISTORY = 20;
  var API_TIMEOUT = 30000;

  // Build system prompt dynamically from store config
  function buildSystemPrompt(config) {
    return [
      'You are ' + config.storeName + ' AI Product Advisor — a knowledgeable trade-supplies assistant for a UK-based e-commerce store. ' + config.storeName + ' specialises in British General (BG) electrical wiring accessories, with smaller ranges in hotel housekeeping, fire-stop products and bathroom/sanitaryware.',
      '',
      'WHAT WE ACTUALLY SELL (do not claim anything outside this list):',
      '- Electrical wiring accessories — almost our entire catalogue is British General (BG): light switches, sockets, USB sockets, dimmer switches, fused connection units (spurs), cooker switches, fan isolators, double-pole switches, grid systems, back boxes, blanking plates and euro modules. Finishes include white moulded, brushed steel, matt black, polished chrome, black nickel, black chrome and antique brass; ranges include BG Nexus, BG Evolve and screwless flatplate.',
      '- Hotel & housekeeping — Corby of Windsor (trouser presses, kettles, irons, hospitality trays, guestroom items).',
      '- Fire safety — Astroflame intumescent fire & smoke seals and fire-stop products.',
      '- A limited bathroom & sanitaryware range — brands such as Geberit, Grohe, Ideal Standard, RAK Ceramics, Vitra and Salamander (cisterns, flush plates, taps, wastes, shower pumps).',
      '',
      'CRITICAL RULES — accuracy matters more than being helpful:',
      '- We DO NOT sell Schneider, Hamilton, MK, Aqualisa, Knightsbridge, Wago, Click/Scolmore, or any brand not listed above. If a customer asks for one, say honestly that we specialise in British General (BG) and offer the closest BG equivalent — never claim we stock a brand we do not.',
      '- NEVER state a specific product code/SKU, price, or stock/availability unless it is given to you in a "RELEVANT PRODUCTS FROM OUR CATALOGUE" block in the conversation. If you do not have that data, do NOT guess — help the customer narrow down and point them to the right collection or to site search at https://' + config.domain + '/search?q=YOUR+TERMS.',
      '- When a "RELEVANT PRODUCTS FROM OUR CATALOGUE" block IS provided, recommend ONLY products from it and use their exact names, prices and links. Do not invent extra products.',
      '- Do not promise stock levels, delivery dates or same-day dispatch for any specific item — only mention our general delivery policy below.',
      '',
      'HOW TO ADVISE:',
      '- Always consider BS 7671 (18th Edition) wiring regulations when recommending electrical products.',
      '- For bathroom electrical queries: mention IP ratings (IP44 minimum for Zone 2, IP65+ for Zone 1); for outdoor, recommend IP65+.',
      '- For dimmers: ask about load type (LED vs incandescent/halogen) and wattage, and whether 1-way or 2-way, then point to our BG dimmer range.',
      '- For flush plates and cisterns: a flush plate must match the cistern/frame series — ask which cistern or frame the customer has before recommending.',
      '- For installation: recommend a qualified electrician for Part P notifiable work, and a G3-certified plumber for unvented hot water.',
      '- Keep responses concise (2-4 sentences unless detail is needed). Use British English spellings. Format product names in **bold**.',
      '',
      'STORE FACTS (these are true and may be stated):',
      '- ' + config.storeName + ' (' + config.domain + ') — UK trade supplier, British General (BG) electrical specialist.',
      '- Sister stores: ' + (config.sisterStores || 'See our other stores') + '.',
      '- Trade accounts available — up to 20% discount for approved trade accounts, plus volume discounts on bulk orders.',
      '- Free standard delivery on orders over £50 (most of the UK).',
      '- Orders placed before 12pm on a working day are prioritised for same-day dispatch where stock allows. We dispatch Monday to Friday only, never at weekends or on bank holidays (general policy only — never promise it for a specific item).'
    ].join('\n');
  }

  function AdvisorWidget(container) {
    this.container = container;
    this.panel = container.querySelector('[data-advisor-panel]');
    this.trigger = container.querySelector('[data-advisor-trigger]');
    this.closeBtn = container.querySelector('[data-advisor-close]');
    this.messages = container.querySelector('[data-advisor-messages]');
    this.form = container.querySelector('[data-advisor-form]');
    this.input = container.querySelector('[data-advisor-input]');
    this.quickActions = container.querySelector('[data-advisor-quick-actions]');
    this.chatIcon = container.querySelector('.kt-advisor__trigger-icon--chat');
    this.closeIcon = container.querySelector('.kt-advisor__trigger-icon--close');

    this.apiHost = container.dataset.apiHost || '';
    this.customerId = container.dataset.customerId || '';
    this.customerName = container.dataset.customerName || '';
    this.isLoggedIn = container.dataset.loggedIn === 'true';
    this.store = container.dataset.store || 'kt';

    // Parse store config from data attribute (avoids hardcoding per store)
    try {
      this.storeConfig = JSON.parse(container.dataset.config || '{}');
    } catch (e) {
      this.storeConfig = {};
    }
    this.storeConfig.storeName = this.storeConfig.storeName || 'Kent Traders';
    this.storeConfig.contactEmail = this.storeConfig.contactEmail || 'support@kenttraders.co.uk';
    this.storeConfig.domain = this.storeConfig.domain || 'kenttraders.co.uk';
    this.storeConfig.sisterStores = this.storeConfig.sisterStores || '';

    this.conversationId = this.generateId();
    this.history = this.loadHistory();
    this.isOpen = false;
    this.isLoading = false;

    this.init();
  }

  AdvisorWidget.prototype.init = function() {
    var self = this;

    // Restore previous messages
    if (this.history.length > 0) {
      this.history.forEach(function(msg) {
        self.appendMessage(msg.role, msg.content, true);
      });
      this.hideQuickActions();
    }

    // Event listeners
    this.trigger.addEventListener('click', function() { self.toggle(); });
    this.closeBtn.addEventListener('click', function() { self.close(); });

    this.form.addEventListener('submit', function(e) {
      e.preventDefault();
      self.sendMessage();
    });

    // Quick action chips
    if (this.quickActions) {
      this.quickActions.addEventListener('click', function(e) {
        var chip = e.target.closest('[data-advisor-chip]');
        if (!chip) return;
        var value = chip.dataset.advisorChip;
        // "Find a product" is a deterministic category menu — render it
        // statically with no AI call (saves a Claude request per click).
        if (value === 'Find a product') {
          self.hideQuickActions();
          self.appendMessage('user', value);
          self.addToHistory('user', value);
          self.showProductMenu();
          return;
        }
        self.input.value = value;
        self.sendMessage();
      });
    }

    // Keyboard: Escape closes
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });

    // Click outside closes
    document.addEventListener('click', function(e) {
      if (self.isOpen && !self.container.contains(e.target)) {
        self.close();
      }
    });

    // Gather product context if on product page
    this.productContext = this.getProductContext();
    this.keepPurchaseControlsClear();
  };

  // The theme inserts its sticky purchase bar after initial page load. Measure
  // that existing bar rather than assuming a fixed height or moving its controls.
  AdvisorWidget.prototype.keepPurchaseControlsClear = function() {
    var container = this.container;
    var selector = '.t4s-sticky-atc';
    var bar = null;
    var queued = false;
    var resize = new ResizeObserver(schedule);

    function update() {
      queued = false;
      var next = document.querySelector(selector);
      if (next !== bar) {
        if (bar) resize.unobserve(bar);
        bar = next;
        if (bar) resize.observe(bar);
      }
      var clearance = 0;
      if (bar && !bar.hidden && bar.classList.contains('is--shown') && bar.offsetHeight > 0) {
        // Use the resting geometry, so the slide-in transition cannot briefly
        // position the launcher inside the purchase bar.
        clearance = bar.offsetHeight + (parseFloat(getComputedStyle(bar).bottom) || 0) + 12;
      }
      container.style.setProperty('--advisor-purchase-clearance', clearance + 'px');
    }
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    }
    function containsBar(node) {
      return node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector));
    }
    new MutationObserver(function(records) {
      if (records.some(function(record) {
        return (record.type === 'attributes' && record.target.matches(selector)) ||
          Array.from(record.addedNodes).some(containsBar) ||
          Array.from(record.removedNodes).some(containsBar);
      })) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    window.addEventListener('resize', schedule);
    update();
  };

  AdvisorWidget.prototype.toggle = function() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  AdvisorWidget.prototype.open = function() {
    this.isOpen = true;
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.chatIcon.style.display = 'none';
    this.closeIcon.style.display = 'block';
    this.input.focus();
    this.scrollToBottom();
  };

  AdvisorWidget.prototype.close = function() {
    this.isOpen = false;
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.chatIcon.style.display = 'block';
    this.closeIcon.style.display = 'none';
  };

  AdvisorWidget.prototype.sendMessage = function() {
    var text = this.input.value.trim();
    if (!text || this.isLoading) return;

    this.input.value = '';
    this.hideQuickActions();
    this.appendMessage('user', text);
    this.addToHistory('user', text);
    this.showTyping();
    this.isLoading = true;

    var self = this;
    this.callAI(text)
      .then(function(response) {
        self.removeTyping();
        self.appendMessage('bot', response);
        self.addToHistory('assistant', response);
        self.isLoading = false;
      })
      .catch(function() {
        self.removeTyping();
        self.appendMessage('bot', 'Sorry, I had trouble processing that. Please try again or contact us at ' + self.storeConfig.contactEmail + ' for assistance.');
        self.isLoading = false;
      });
  };

  AdvisorWidget.prototype.callAI = function(message) {
    // Build messages array with system prompt and history
    var messages = [];

    // Add conversation history (last 10 exchanges)
    var recentHistory = this.history.slice(-10);
    recentHistory.forEach(function(msg) {
      messages.push({
        role: msg.role === 'bot' ? 'assistant' : msg.role,
        content: msg.content
      });
    });

    // Build the user message with product context
    var userMessage = message;
    if (this.productContext) {
      userMessage = '[Customer is viewing: ' + this.productContext + ']\n\n' + message;
    }
    if (this.customerName) {
      userMessage = '[Customer: ' + this.customerName + (this.isLoggedIn ? ' (logged in)' : '') + ']\n\n' + userMessage;
    }

    messages.push({ role: 'user', content: userMessage });

    // Call the AIMarketOps advisor endpoint. We intentionally do NOT pass
    // ?storeId= here — the AIMarketOps route reads the storeId via env
    // fallback (DEFAULT_STORE_ID, which resolves to the KT prod CUID), and
    // the Phase 5 gate (phase5.ai-advisor) keys on that CUID. Passing the
    // slug ("kt") would mismatch the DB row and force a permanent 503.
    // Re-enable when AIMarketOps adopts a slug→CUID alias map in
    // app/api/ai/product-advisor/route.ts (mirrors STORE_ALIASES from
    // scripts/enable-phase5-features.ts).
    var apiUrl = this.apiHost + '/api/ai/product-advisor';
    var body = {
      messages: messages,
      systemPrompt: buildSystemPrompt(this.storeConfig),
      conversationId: this.conversationId,
      store: this.store,
      customerId: this.customerId
    };

    return fetchWithTimeout(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, API_TIMEOUT)
      .then(function(res) {
        if (!res.ok) throw new Error('API error ' + res.status);
        return res.json();
      })
      .then(function(data) {
        return data.response || data.message || 'I apologise, I could not generate a response. Please try again.';
      });
  };

  AdvisorWidget.prototype.appendMessage = function(role, content, skipScroll) {
    var msgDiv = document.createElement('div');
    msgDiv.className = 'kt-advisor__msg kt-advisor__msg--' + (role === 'user' ? 'user' : 'bot');

    var contentDiv = document.createElement('div');
    contentDiv.className = 'kt-advisor__msg-content';

    // Basic markdown-like formatting: **bold**, product links
    var formatted = this.formatMessage(content);
    contentDiv.innerHTML = formatted;

    msgDiv.appendChild(contentDiv);
    this.messages.appendChild(msgDiv);

    if (!skipScroll) this.scrollToBottom();
  };

  AdvisorWidget.prototype.formatMessage = function(text) {
    // Escape HTML first
    var escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Bold: **text**
    escaped = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Line breaks
    escaped = escaped.replace(/\n/g, '<br>');

    // Auto-link product URLs (uses store domain from config)
    var linkDomain = (this.storeConfig.domain || 'kenttraders.co.uk').replace(/\./g, '\\.');
    var linkRegex = new RegExp('(https?:\\/\\/' + linkDomain + '\\/[^\\s<]+)', 'g');
    escaped = escaped.replace(
      linkRegex,
      '<a href="$1" target="_blank" rel="noopener" class="kt-advisor__product-card-link">View Product</a>'
    );

    return escaped;
  };

  AdvisorWidget.prototype.showTyping = function() {
    var typingDiv = document.createElement('div');
    typingDiv.className = 'kt-advisor__typing';
    typingDiv.setAttribute('data-advisor-typing', '');
    typingDiv.innerHTML = '<span class="kt-advisor__typing-dot"></span><span class="kt-advisor__typing-dot"></span><span class="kt-advisor__typing-dot"></span>';
    this.messages.appendChild(typingDiv);
    this.scrollToBottom();
  };

  AdvisorWidget.prototype.removeTyping = function() {
    var typing = this.messages.querySelector('[data-advisor-typing]');
    if (typing) typing.remove();
  };

  AdvisorWidget.prototype.hideQuickActions = function() {
    if (this.quickActions) this.quickActions.style.display = 'none';
  };

  // Append a bot message from pre-built, trusted HTML (no user input is
  // interpolated here, so innerHTML is safe and we keep real category links).
  AdvisorWidget.prototype.appendBotHtml = function(html) {
    var msgDiv = document.createElement('div');
    msgDiv.className = 'kt-advisor__msg kt-advisor__msg--bot';
    var contentDiv = document.createElement('div');
    contentDiv.className = 'kt-advisor__msg-content';
    contentDiv.innerHTML = html;
    msgDiv.appendChild(contentDiv);
    this.messages.appendChild(msgDiv);
    this.scrollToBottom();
  };

  // Static "Find a product" category menu — deterministic, so it needs no AI.
  // Links point at real, high-volume Kent Traders collections (BG-electrical
  // first, matching our actual catalogue).
  AdvisorWidget.prototype.showProductMenu = function() {
    var base = 'https://' + (this.storeConfig.domain || 'kenttraders.co.uk');
    var cats = [
      ['Light switches & sockets', '/collections/bg-nexus-switches-sockets'],
      ['Dimmer switches', '/collections/bg-touch-dimmers'],
      ['Screwless flatplate', '/collections/flatplate-screwless'],
      ['Grid switch systems', '/collections/bg-nexus-grid'],
      ['Browse all British General', '/collections/british-general'],
      ['Bathroom & sanitaryware', '/collections/sanitary-supplies'],
      ['Fire seals (Astroflame)', '/search?q=astroflame'],
      ['Hotel supplies (Corby of Windsor)', '/search?q=corby']
    ];
    var links = cats.map(function(c) {
      return '<li><a href="' + base + c[1] + '" target="_blank" rel="noopener" class="kt-advisor__menu-link">' + c[0] + '</a></li>';
    }).join('');
    var html =
      '<p>Sure — what are you after? Pick a category:</p>' +
      '<ul class="kt-advisor__menu">' + links + '</ul>' +
      '<p>Or just type what you need — e.g. <em>&ldquo;matt black double socket&rdquo;</em> or <em>&ldquo;2-way LED dimmer&rdquo;</em> — and I&rsquo;ll find it.</p>';
    this.appendBotHtml(html);
  };

  AdvisorWidget.prototype.scrollToBottom = function() {
    var self = this;
    requestAnimationFrame(function() {
      self.messages.scrollTop = self.messages.scrollHeight;
    });
  };

  AdvisorWidget.prototype.getProductContext = function() {
    var contextEl = this.container.querySelector('[data-advisor-context] [data-product-title]');
    if (!contextEl) return null;

    var title = contextEl.dataset.productTitle || '';
    var type = contextEl.dataset.productType || '';
    var vendor = contextEl.dataset.productVendor || '';
    var price = contextEl.dataset.productPrice || '';
    var tags = contextEl.dataset.productTags || '';

    var parts = [title];
    if (vendor) parts.push('by ' + vendor);
    if (type) parts.push('(' + type + ')');
    if (price) parts.push('at ' + price);
    if (tags) {
      // Extract IP rating and compliance tags
      var tagList = tags.split(',');
      var relevantTags = tagList.filter(function(t) {
        return t.indexOf('ip') === 0 || t.indexOf('bs-') === 0 || t.indexOf('zone') === 0;
      });
      if (relevantTags.length > 0) parts.push('[' + relevantTags.join(', ') + ']');
    }

    return parts.join(' ');
  };

  // History management
  AdvisorWidget.prototype.addToHistory = function(role, content) {
    this.history.push({ role: role, content: content, timestamp: Date.now() });
    if (this.history.length > MAX_HISTORY) {
      this.history = this.history.slice(-MAX_HISTORY);
    }
    this.saveHistory();
  };

  AdvisorWidget.prototype.loadHistory = function() {
    try {
      var data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  };

  AdvisorWidget.prototype.saveHistory = function() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.history));
    } catch (e) {
      // Storage full or unavailable
    }
  };

  AdvisorWidget.prototype.generateId = function() {
    return 'adv_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  };

  // Fetch with timeout
  function fetchWithTimeout(url, options, timeout) {
    return new Promise(function(resolve, reject) {
      var controller = new AbortController();
      var timer = setTimeout(function() {
        controller.abort();
        reject(new Error('Request timed out'));
      }, timeout);

      fetch(url, Object.assign({}, options, { signal: controller.signal }))
        .then(function(res) {
          clearTimeout(timer);
          resolve(res);
        })
        .catch(function(err) {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  // Initialize on DOM ready
  function init() {
    var container = document.getElementById('kt-product-advisor');
    if (container) {
      new AdvisorWidget(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
